/**
 * Cleovivra: scroll-driven selection for collection links.
 *
 * Stock Horizon only moves the active link on hover — its internal scroll
 * handler measures horizontal centres, so it drives the carousel and never
 * fires from scrolling down the page.
 *
 * The brand reference is purely scroll-driven: collection images are stacked in
 * a vertical column (see cleo-collection-links.css) and the highlight follows
 * whichever image currently fills the frame. This mirrors that by measuring the
 * stacked slides against the viewport centre.
 *
 * Deliberately no hover suppression: while you scroll, the pointer is almost
 * always inside the section, so gating on hover swallowed every scroll update
 * and made the section look like it only responded to hovering the labels.
 */

const TAG = 'collection-links-component';

function enhance(component) {
  const container = component.querySelector('.collection-links__container');
  const imagesRoot = component.querySelector('.collection-links__images');
  if (!container || !imagesRoot) return;

  // Scoped to .collection-links__link so the added DISCOVER anchor and any
  // other chrome inside the container aren't mistaken for a collection.
  const links = Array.from(container.querySelectorAll('a.collection-links__link'));
  const slides = Array.from(imagesRoot.querySelectorAll('slideshow-slide'));
  if (links.length < 2 || slides.length === 0) return;

  let queued = false;
  let lastIndex = -1;

  const apply = (index) => {
    for (const [i, link] of links.entries()) {
      link.setAttribute('aria-current', String(i === index));
    }
  };

  const update = () => {
    queued = false;
    // The theme editor replaces section markup wholesale; bail on stale nodes.
    if (!component.isConnected) return;

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const focalPoint = viewportH / 2;

    // Whichever stacked image straddles the middle of the screen wins.
    let bestIndex = 0;
    let bestDistance = Infinity;

    slides.forEach((slide, index) => {
      if (index >= links.length) return;
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - focalPoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    if (bestIndex === lastIndex) return;
    lastIndex = bestIndex;
    apply(bestIndex);
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  // Horizon scrolls div.page-wrapper on desktop but the document on mobile,
  // so listen to both rather than picking one.
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  update();
}

const enhanced = new WeakSet();

function enhanceAll(root = document) {
  const scope = root instanceof Element && root.matches(TAG) ? [root] : root.querySelectorAll(TAG);
  for (const component of scope) {
    if (enhanced.has(component)) continue;
    enhanced.add(component);
    enhance(component);
  }
}

customElements.whenDefined(TAG).then(() => enhanceAll());

/*
 * The theme editor re-renders a section in place when you change its settings —
 * adding or removing a collection swaps in fresh markup that never ran through
 * enhance(), so without this the scroll behaviour silently dies while editing.
 */
document.addEventListener('shopify:section:load', (event) => {
  customElements.whenDefined(TAG).then(() => enhanceAll(event.target));
});
